import { createSessionObservationCoordinator, IDLE_SESSION_OBSERVATION, type SessionObservationState } from '@borgee/remote-control-client';
import { createApplicationRequests, type ApplicationRequests } from './application-requests';
import { normalizeActivityFrame } from './normalized-activity';
import { useDshSessionStore } from '../stores/useDshSessionStore';
import { parseSessionObservationResult, validSessionObservationInput, type SessionObservationInput, type SessionObservationResult, type SessionAvailability } from '@borgee/plugin-sdk/bpp-normalized';
import { getSessionObservationTarget } from './api';
import { dshSessionKey, type DshSessionScope } from './dsh-session-journal';
import { resolveNormalizedSessionScope } from './normalized-session-scope';

type Socket = Pick<WebSocket, 'readyState' | 'send'>;
interface Connection {
  supported(): boolean;
  enable(value: boolean): void;
  syncState(channelId: string, agentId: string): Promise<void>;
  submit(input: SessionObservationInput, expiresAt?: number): Promise<SessionObservationResult>;
  receive(frame: Record<string, unknown>): void;
  route(frame: Record<string, unknown>, deliver: (scope: DshSessionScope, frame: Record<string, unknown>) => void): boolean;
  sync(scope: DshSessionScope): Promise<void>;
  recover(scope: DshSessionScope, epoch: string): void;
  attach(scope: DshSessionScope): () => void;
  availability(scope: DshSessionScope, frame: SessionAvailability): void;
  snapshot(scope: DshSessionScope): SessionObservationState;
  resumeChannel(channelId: string): void;
  releaseChannel(channelId: string): void;
  close(): void;
}
let active: Connection | undefined;
const listeners = new Set<() => void>();
const notify = () => { for (const listener of listeners) { try { listener(); } catch { /* A failed reader cannot prevent other views from updating. */ } } };
export const subscribeSessionObservation = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export const sessionObservationSnapshot = (scope: DshSessionScope) => active?.snapshot(scope) ?? IDLE_SESSION_OBSERVATION;
export const attachSessionTimeline = (scope: DshSessionScope) => active?.attach(scope);
export const syncNormalizedActivity = (channelId: string, agentId: string) => active?.syncState(channelId, agentId) ?? Promise.reject(new Error('session_observation_unavailable'));
export const sessionObservationSupported = () => active?.supported() ?? false;
export const syncSessionTimeline = (scope: DshSessionScope) => active?.sync(scope) ?? Promise.reject(new Error('session_observation_unavailable'));
export const recoverSessionTimeline = (scope: DshSessionScope, epoch: string) => active?.recover(scope, epoch);
export const resumeSessionObservationChannel = (channelId: string) => active?.resumeChannel(channelId);
export const releaseSessionObservationChannel = (channelId: string) => active?.releaseChannel(channelId);

/** Socket ownership bounds recovery requests without owning native Sessions. */
export function bindSessionObservationSocket(socket: Socket, requests: ApplicationRequests = createApplicationRequests(socket)): Connection {
  active?.close();
  const releasedChannels = new Set<string>();
  const channelEpochs = new Map<string, number>();
  let closed = false;
  const connection: Connection = {
    supported: () => !closed && requests.supported(),
    enable(value) { requests.enable(value); coordinator.refresh(); },
    async syncState(channelId, agentId) {
      if (!connection.supported() || releasedChannels.has(channelId)) throw new Error('session_observation_unavailable');
      try {
        const result = await requests.request<{ synced?: unknown }>(channelId, agentId, 'sync_normalized_activity');
        if (result?.synced !== true) throw new Error('invalid_sync_result');
      } catch (error) { throw new Error(error instanceof Error && error.message === 'application_connection_unavailable' ? 'session_observation_unavailable' : 'normalized_activity_sync_unconfirmed'); }
    },
    async submit(input, expiresAt) {
      if (!connection.supported() || releasedChannels.has(input.channel_id) || !validSessionObservationInput(input)) throw new Error('session_observation_unavailable');
      const captured = structuredClone(input);
      let result: unknown;
      try { result = await requests.request(captured.channel_id, captured.agent_id, 'session_observation', captured, { id: captured.operation_id, priority: true, expiresAt }); }
      catch (error) { throw new Error(error instanceof Error && error.message === 'invalid_application_response' ? 'invalid_session_observation_result' : 'session_observation_unconfirmed'); }
      return parseSessionObservationResult(result, captured);
    },
    receive(frame) { requests.receive(frame); },
    route(frame, deliver) {
      if (frame.session_control_binding_id === undefined) return false;
      if (closed || typeof frame.session_control_binding_id !== 'string' || !/^[A-Za-z0-9_:.@/-]{1,512}$/.test(frame.session_control_binding_id) || typeof frame.channel_id !== 'string' || typeof frame.agent_id !== 'string') return true;
      const owner = resolveNormalizedSessionScope(frame.channel_id, frame.agent_id);
      if (owner) {
        deliver({ ...owner, bindingId: frame.session_control_binding_id }, frame);
        if (frame.session_control_default === true) {
          const normalized = normalizeActivityFrame({ ...frame, task_id: typeof frame.task_id === 'string' ? frame.task_id : '' });
          if (normalized?.event.type === 'runtime_updated' || normalized?.event.type === 'agent_snapshot') useDshSessionStore.getState().notifyDefaultSelection(owner);
        }
      }
      return true;
    },
    sync: scope => coordinator.retry(scope),
    recover: (scope, epoch) => coordinator.gap(scope, epoch),
    attach: scope => coordinator.attach(scope),
    snapshot: scope => coordinator.snapshot(scope),
    availability(scope, frame) {
      useDshSessionStore.getState().receiveAvailability(scope, frame);
      coordinator.availability(scope);
    },
    resumeChannel(channelId) { if (!closed) { releasedChannels.delete(channelId); requests.resume(channelId); coordinator.refresh(); } },
    releaseChannel(channelId) {
      releasedChannels.add(channelId);
      channelEpochs.set(channelId, (channelEpochs.get(channelId) ?? 0) + 1);
      requests.release(channelId);
      coordinator.releaseChannel(channelId);
    },
    close() {
      if (closed) return;
      closed = true;
      unsubscribe();
      if (active === connection) active = undefined;
      requests.close();
      coordinator.close(); releasedChannels.clear(); notify();
    },
  };
  const coordinator = createSessionObservationCoordinator({
    getView: scope => useDshSessionStore.getState().sessions[dshSessionKey(scope)],
    needsTimelineSync: scope => useDshSessionStore.getState().needsTimelineSync(scope),
    isAvailable: scope => connection.supported() && !releasedChannels.has(scope.channelId),
    async synchronize(scope) {
      const current = useDshSessionStore.getState().sessions[dshSessionKey(scope)];
      const channelEpoch = channelEpochs.get(scope.channelId) ?? 0;
      const observationGeneration = current?.observationGeneration;
      const availabilityRevision = current?.observationAvailability?.revision;
      if (!connection.supported() || releasedChannels.has(scope.channelId)) throw new Error('session_observation_unavailable');
      const controller = new AbortController();
      const expiresAt = Date.now() + 15000;
      const timer = setTimeout(() => controller.abort(), Math.max(0, expiresAt - Date.now()));
      try {
        const target = await getSessionObservationTarget(scope.channelId, scope.agentId, controller.signal, scope.bindingId, expiresAt);
        const session = useDshSessionStore.getState().sessions[dshSessionKey(scope)];
        if (!connection.supported() || releasedChannels.has(scope.channelId) || (channelEpochs.get(scope.channelId) ?? 0) !== channelEpoch || session?.observationGeneration !== observationGeneration || session?.observationAvailability?.revision !== availabilityRevision || !session?.servicePaused && (session?.sessionControlBindingId !== target.binding_id || session.runtimeInfo?.sessionId !== target.native_session_id) || scope.bindingId && scope.bindingId !== target.binding_id) throw new Error('stale_session_binding');
        const input: SessionObservationInput = { operation_id: crypto.randomUUID(), operation: 'sync_timeline', channel_id: target.channel_id, agent_id: target.agent_id, binding_id: target.binding_id, native_session_id: target.native_session_id, connection_generation: target.connection_generation };
        let reason: string | undefined;
        if (!await useDshSessionStore.getState().syncTimeline(scope, input.operation_id, async () => {
          const result = await connection.submit(input, expiresAt);
          if (result.status !== 'confirmed') reason = result.reason;
          return result;
        }, undefined, target.native_session_id)) throw new Error(reason ?? 'timeline_sync_unconfirmed');
      } finally { clearTimeout(timer); }
    },
  });
  coordinator.subscribe(notify);
  const unsubscribe = useDshSessionStore.subscribe(() => coordinator.refresh());
  active = connection;
  notify();
  return connection;
}
